import React, { useState } from 'react';
import axios from 'axios';
import CustomerLayout from '../../components/layouts/CustomerLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Search, MapPin, Clock, Truck, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tracking = () => {
  const [reference, setReference] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return;

    setLoading(true);
    setError('');
    setTrackingData(null);

    try {
      const response = await axios.get(`${API_URL}/api/track/${reference.trim()}`);
      setTrackingData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Waybill not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusOrder = ['quote_requested', 'in_costing', 'quote_ready', 'accepted', 'dispatch_scheduled', 'collected', 'in_transit', 'at_customs', 'out_for_delivery', 'delivered'];
  
  const getProgress = (status) => {
    const idx = statusOrder.indexOf(status);
    return idx >= 0 ? ((idx + 1) / statusOrder.length) * 100 : 0;
  };

  return (
    <CustomerLayout>
      <div className="animate-fade-in" data-testid="tracking-page">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="page-title">Track Shipment</h1>
            <p className="text-slate-600 mt-2">Enter your waybill reference number to track your shipment</p>
          </div>

          {/* Search Form */}
          <Card className="card-elevated mb-8">
            <CardContent className="p-6">
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Enter waybill number (e.g., WB-7729-ZA)"
                    className="pl-10 h-12 text-lg"
                    data-testid="tracking-search-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="btn-primary h-12 px-8" data-testid="tracking-search-btn">
                  {loading ? 'Searching...' : 'Track'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-8 text-center text-red-700">
              {error}
            </div>
          )}

          {/* Results */}
          {trackingData && (
            <div className="space-y-6" data-testid="tracking-results">
              {/* Status Overview */}
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                      <p className="text-sm text-slate-500">Reference Number</p>
                      <p className="text-2xl font-bold text-slate-900">{trackingData.reference_number}</p>
                      <p className="text-sm text-slate-600 mt-1">{trackingData.client_name}</p>
                    </div>
                    <StatusBadge status={trackingData.status} className="text-base px-4 py-2" />
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-teal-600 transition-all duration-500"
                        style={{ width: `${getProgress(trackingData.status)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex items-center justify-between py-4 border-t border-slate-100">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-teal-50 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Origin</p>
                        <p className="font-semibold text-slate-900">{trackingData.origin_city}</p>
                      </div>
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="border-t-2 border-dashed border-slate-200"></div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div>
                        <p className="text-sm text-slate-500 text-right">Destination</p>
                        <p className="font-semibold text-slate-900">{trackingData.destination_city}</p>
                      </div>
                      <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center">
                        <Package className="h-5 w-5 text-purple-600" />
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-sm text-slate-500">Sender</p>
                      <p className="font-medium text-slate-900">{trackingData.sender_company}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Receiver</p>
                      <p className="font-medium text-slate-900">{trackingData.receiver_company}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Est. Delivery</p>
                      <p className="font-medium text-teal-700">{formatDate(trackingData.estimated_delivery_date)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card className="card-elevated">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-indigo-600" />
                    <CardTitle className="card-title">Shipment Timeline</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {trackingData.tracking_events?.length > 0 ? (
                    <div className="space-y-4">
                      {trackingData.tracking_events.map((event, idx) => (
                        <div key={event.event_id || `te-${idx}`} className="flex">
                          <div className="flex flex-col items-center mr-4">
                            <div className={`w-4 h-4 rounded-full border-2 ${idx === 0 ? 'bg-teal-600 border-teal-600' : 'bg-white border-slate-300'}`}></div>
                            {idx < trackingData.tracking_events.length - 1 && (
                              <div className="w-0.5 h-full bg-slate-200 my-1"></div>
                            )}
                          </div>
                          <div className="pb-6">
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusBadge status={event.status} />
                              <span className="text-sm text-slate-500">{formatDateTime(event.timestamp)}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 mt-1">{event.location}</p>
                            {event.notes && <p className="text-sm text-slate-600 mt-1">{event.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No tracking events available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default Tracking;
